/* eslint-disable @nx/enforce-module-boundaries */
import { AspectRatio, buildDynamicRenditionPath, HeightRatioFromAspectRatio } from '@marriott/mi-web-ui-kit';

/** Aspect ratio values for use by slotPropNormalizers (avoids mi-web-ui-kit import in product-components) */
export const ASPECT_RATIOS = {
  CLASSIC_HOR: AspectRatio.CLASSIC_HOR,
  SQUARE: AspectRatio.SQUARE,
  WIDE_HOR: AspectRatio.WIDE_HOR,
  CLASSIC_VER: AspectRatio.CLASSIC_VER,
} as const;

type CssMediaQueryType = 'min-width' | 'max-width';

export interface LayeredFeatureRendition {
  renditionPath: string;
  dynamic: boolean;
  mediaValue: string;
  mediaQuery: CssMediaQueryType;
  damPath: string;
  width: number;
}

type LayeredFeatureDcaUrlKey = 'panoramicHorizontal' | 'standardHorizontal' | 'square';

interface LayeredFeatureRenditionSpec {
  breakpointPx: number;
  imgWidthPx: number;
  aspectRatio: AspectRatio;
  dcaUrlKey: LayeredFeatureDcaUrlKey;
}

export const LAYERED_FEATURE_RENDITION_SPECS: LayeredFeatureRenditionSpec[] = [
  // Desktop: 992+
  { breakpointPx: 1200, imgWidthPx: 1140, aspectRatio: AspectRatio.PANO_HOR, dcaUrlKey: 'panoramicHorizontal' },
  { breakpointPx: 992, imgWidthPx: 960, aspectRatio: AspectRatio.STANDARD_HOR, dcaUrlKey: 'standardHorizontal' },
  // Tablet: 768-991
  { breakpointPx: 768, imgWidthPx: 720, aspectRatio: AspectRatio.STANDARD_HOR, dcaUrlKey: 'standardHorizontal' },
  // Mobile: 0-767
  { breakpointPx: 576, imgWidthPx: 540, aspectRatio: AspectRatio.SQUARE, dcaUrlKey: 'square' },
  { breakpointPx: 0, imgWidthPx: 343, aspectRatio: AspectRatio.SQUARE, dcaUrlKey: 'square' },
];

function buildDynamicSrcSetFromUrl(url: string, widthPx: number): string {
  const querySeparator = url.includes('?') ? '&' : '?';
  const first = `${url}${querySeparator}wid=${widthPx}&fit=constrain`;
  const second = `${url}${querySeparator}wid=${widthPx * 2}&fit=constrain 2x`;
  return `${first}, ${second}`;
}

function buildLayeredFeatureRenditions(
  renditionPathResolver: (spec: LayeredFeatureRenditionSpec) => string | null,
  damPath: string
): LayeredFeatureRendition[] {
  return LAYERED_FEATURE_RENDITION_SPECS.map(spec => {
    const renditionPath = renditionPathResolver(spec);
    if (!renditionPath) {
      return null;
    }

    return {
      renditionPath,
      dynamic: true,
      mediaQuery: 'min-width',
      mediaValue: `${spec.breakpointPx}px`,
      damPath,
      width: spec.imgWidthPx,
    };
  }).filter(Boolean) as LayeredFeatureRendition[];
}

export function buildLayeredFeatureDynamicMediaNode(assetUrl?: string, altText?: string) {
  if (!assetUrl) return;

  return {
    altText: altText || '',
    assetPath: assetUrl,
    enableSmartCrop: true,
    dynamic: true,
    renditions: buildLayeredFeatureRenditions(
      spec => buildDynamicRenditionPath(assetUrl, spec.aspectRatio, spec.imgWidthPx),
      assetUrl
    ),
    damPath: assetUrl,
  };
}

export function buildLayeredFeatureDcaRenditions(
  imageUrls?: Partial<Record<LayeredFeatureDcaUrlKey, string | null>>,
  damPath = ''
): LayeredFeatureRendition[] {
  if (!imageUrls) return [];

  return buildLayeredFeatureRenditions(spec => {
    const baseUrl = imageUrls[spec.dcaUrlKey];
    if (!baseUrl) return null;
    return buildDynamicSrcSetFromUrl(baseUrl, spec.imgWidthPx);
  }, damPath);
}

export const buildDynamicMediaNode = (assetUrl?: string, aspectRatio?: AspectRatio, altText?: string) => {
  if (!assetUrl) return;

  const widthXL = 1200;
  const widthLG = 992;
  const widthMD = 768;
  const widthSM = 576;
  const widthXS = 0;
  const imgWidthXS = 576;

  const ratio = (HeightRatioFromAspectRatio.get(aspectRatio || AspectRatio.SQUARE) as number) || 1;

  const xlSrcSet = buildDynamicRenditionPath(assetUrl, aspectRatio, widthXL);
  const lgSrcSet = buildDynamicRenditionPath(assetUrl, aspectRatio, widthLG);
  const mdSrcSet = buildDynamicRenditionPath(assetUrl, aspectRatio, widthMD);
  const smSrcSet = buildDynamicRenditionPath(assetUrl, aspectRatio, widthSM);
  const xsSrcSet = buildDynamicRenditionPath(assetUrl, aspectRatio, imgWidthXS);

  return {
    altText: altText || '',
    assetPath: assetUrl,
    enableSmartCrop: true,
    dynamic: true,
    renditions: [
      {
        renditionPath: xlSrcSet,
        dynamic: true,
        mediaValue: `${widthXL}px`,
        mediaQuery: 'min-width',
        damPath: assetUrl,
        width: widthXL,
        height: Math.round(widthXL / ratio),
      },
      {
        renditionPath: lgSrcSet,
        dynamic: true,
        mediaValue: `${widthLG}px`,
        mediaQuery: 'min-width',
        damPath: assetUrl,
        width: widthLG,
        height: Math.round(widthLG / ratio),
      },
      {
        renditionPath: mdSrcSet,
        mediaQuery: 'min-width',
        dynamic: true,
        mediaValue: `${widthMD}px`,
        damPath: assetUrl,
        width: widthMD,
        height: Math.round(widthMD / ratio),
      },
      {
        renditionPath: smSrcSet,
        mediaQuery: 'min-width',
        dynamic: true,
        mediaValue: `${widthSM}px`,
        damPath: assetUrl,
        width: widthSM,
        height: Math.round(widthSM / ratio),
      },
      {
        renditionPath: xsSrcSet,
        mediaQuery: 'min-width',
        dynamic: true,
        mediaValue: `${widthXS}px`,
        damPath: assetUrl,
        width: imgWidthXS,
        height: Math.round(imgWidthXS / ratio),
      },
    ],
    damPath: assetUrl,
  };
};
